import React, { FC, useEffect, useRef, useState } from 'react';

import './ReCaptcha.scss';

import useLastCallback from '../../hooks/useLastCallback';
import { FieldProps } from 'formik';
import classNames from 'classnames';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';

type OwnProps = {
  action: string;
};

const ReCaptcha: FC<FieldProps & OwnProps> = ({
  field,
  form: { touched, errors, setFieldValue, setFieldError },
  action,
}) => {
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [check, setCheck] = useState(false);
  const [error, setError] = useState(false);
  const { name, value } = field;

  const handleCheck = useLastCallback(async () => {
    if (!executeRecaptcha) {
      setFieldError(name, 'Execute recaptcha not yet available');
      setError(true);
      setFieldValue(name, '');
      return;
    }
    const token = await executeRecaptcha(action);
    setFieldValue(name, token);
    setError(false);
    setTimeout(() => {
      setFieldValue(name, '');
    }, 120000);
  });

  useEffect(() => {
    if (value) {
      setCheck(true);
    } else {
      setCheck(false);
    }
  }, [value]);

  return (
    <div
      className={classNames('input', {
        error: touched[name] && errors[name],
      })}
    >
      <div className='captcha-container'>
        <div className='captcha-content'>
          <span
            className={classNames('captcha-checkbox-container', {
              checked: check && !error,
              error,
            })}
          >
            <span className='captcha-checkbox' onClick={handleCheck} />
            <span className='captcha-checkbox-checkmark' />
            <span className='captcha-spiner' />
          </span>
          <span>I’m not a robot</span>
        </div>
        <div className='captcha-badge'>
          <Logo />
          <p className='captcha-privacy'>
            <a href='https://policies.google.com/privacy'>Privacy</a>
            {' - '}
            <a href='https://policies.google.com/terms'>Terms</a>
          </p>
        </div>
      </div>

      {touched[name] && errors[name] && (
        <p className='input-notification--error'>{String(errors[name])}</p>
      )}
    </div>
  );
};

export default ReCaptcha;

const Logo = () => {
  return (
    <svg xmlns='http://www.w3.org/2000/svg' width='48' height='46' fill='none'>
      <g clipPath='url(#a)'>
        <path
          fill='#1C3AA9'
          d='M39.307 16.212c0-.225-.006-.448-.016-.67V2.851l-3.508 3.507A15.627 15.627 0 0 0 23.65.6a15.63 15.63 0 0 0-12.474 6.194l5.75 5.81a7.619 7.619 0 0 1 2.33-2.613c1.004-.783 2.426-1.424 4.394-1.424.238 0 .421.028.556.08a7.581 7.581 0 0 1 5.795 3.491l-4.07 4.07c5.156-.02 10.98-.031 13.375.003'
        />
        <path
          fill='#4285F4'
          d='M23.558.6c-.224.001-.448.006-.67.017h-12.69l3.508 3.508a15.627 15.627 0 0 0-5.76 12.132A15.63 15.63 0 0 0 14.14 28.73l5.811-5.75a7.62 7.62 0 0 1-2.613-2.33c-.784-1.004-1.424-2.427-1.424-4.394 0-.238.027-.421.08-.556a7.581 7.581 0 0 1 3.49-5.796l4.071 4.07c-.02-5.155-.032-10.98.003-13.374'
        />
        <path
          fill='#ABABAB'
          d='M7.947 16.256c.001.225.006.449.016.671v12.69l3.509-3.508a15.627 15.627 0 0 0 12.131 5.76c5.092 0 9.615-2.43 12.475-6.194l-5.75-5.811a7.617 7.617 0 0 1-2.33 2.613c-1.004.784-2.427 1.424-4.394 1.424-.238 0-.422-.028-.556-.08a7.581 7.581 0 0 1-5.796-3.49l4.07-4.071c-5.155.02-10.98.032-13.374-.003'
        />
        <path
          fill='#A6A6A6'
          d='M10.589 37.932c-.381 0-.723.072-1.026.217a2.125 2.125 0 0 0-.769.606 2.84 2.84 0 0 0-.478.957 4.446 4.446 0 0 0-.163 1.247v1.327c0 .463.054.881.163 1.252a2.9 2.9 0 0 0 .473.952c.204.263.45.465.74.607.29.141.614.212.976.212.372 0 .697-.054.977-.163.282-.108.519-.264.71-.468a2.12 2.12 0 0 0 .439-.75c.105-.292.167-.624.187-.996h-.912a4.037 4.037 0 0 1-.119.735c-.056.2-.138.366-.246.498a.94.94 0 0 1-.415.286c-.167.059-.374.089-.62.089-.264 0-.487-.058-.671-.173a1.354 1.354 0 0 1-.45-.483 2.441 2.441 0 0 1-.246-.715 4.965 4.965 0 0 1-.074-.883v-1.337c0-.338.03-.645.089-.922.062-.276.156-.51.281-.705.125-.194.283-.343.474-.449.19-.108.417-.162.68-.162.22 0 .41.032.567.098.158.063.29.161.395.296.105.132.185.3.241.503.056.204.094.447.114.73h.912a3.558 3.558 0 0 0-.182-1.03c-.106-.3-.252-.551-.44-.755a1.81 1.81 0 0 0-.685-.464 2.498 2.498 0 0 0-.922-.157Zm22.27 0c-.381 0-.723.072-1.026.217a2.125 2.125 0 0 0-.769.606 2.84 2.84 0 0 0-.478.957 4.45 4.45 0 0 0-.163 1.247v1.327c0 .463.054.881.163 1.252a2.9 2.9 0 0 0 .473.952c.204.263.45.465.74.607.29.141.615.212.976.212.372 0 .697-.054.977-.163.282-.108.52-.264.71-.468a2.12 2.12 0 0 0 .439-.75c.105-.292.167-.624.187-.996h-.912a4.046 4.046 0 0 1-.119.735c-.055.2-.138.366-.246.498a.94.94 0 0 1-.414.286c-.168.059-.375.089-.622.089-.263 0-.486-.058-.67-.173a1.354 1.354 0 0 1-.45-.483 2.44 2.44 0 0 1-.246-.715 4.968 4.968 0 0 1-.074-.883v-1.337c0-.338.03-.645.09-.922.062-.276.155-.51.28-.705.125-.194.283-.343.474-.449.19-.108.417-.162.68-.162.22 0 .41.032.567.098.158.063.29.161.395.296.105.132.186.3.241.503.056.204.094.447.114.73h.912a3.558 3.558 0 0 0-.182-1.03c-.106-.3-.252-.551-.44-.755a1.81 1.81 0 0 0-.685-.464 2.497 2.497 0 0 0-.922-.157Zm-17.072.098-2.313 7.18h.927l.558-1.874h2.426l.567 1.874h.927l-2.318-7.18h-.774Zm4.068 0v7.18h.903V42.4h1.385c.345 0 .651-.048.918-.144a1.74 1.74 0 0 0 1.085-1.105c.095-.272.142-.581.142-.926 0-.32-.047-.612-.143-.878a1.91 1.91 0 0 0-.409-.696 1.824 1.824 0 0 0-.675-.458 2.415 2.415 0 0 0-.918-.163h-2.288Zm5.124 0v.78h1.884v6.4h.902v-6.4h1.889v-.78h-4.675Zm11.327 0v7.18h.903v-3.319h2.89v3.32h.907v-7.18h-.908v3.086h-2.89V38.03h-.902Zm8.122 0-2.313 7.18h.927l.557-1.874h2.427l.567 1.874h.927l-2.318-7.18h-.774Zm-23.67.78h1.385c.22 0 .408.039.562.118.158.079.286.184.385.315.102.132.176.285.222.459.05.17.074.348.074.532 0 .204-.025.392-.074.563-.046.167-.12.314-.222.439a1.001 1.001 0 0 1-.385.286 1.374 1.374 0 0 1-.562.103h-1.386V38.81Zm-4.586.483.98 3.264h-1.957l.977-3.264Zm28.64 0 .982 3.264h-1.958l.977-3.264Zm-42.477.483a.998.998 0 0 0-.587.172 1.382 1.382 0 0 0-.405.469l-.014-.543H.48v5.336h.873v-3.817c.083-.227.2-.406.35-.537a.874.874 0 0 1 .587-.198c.076 0 .143.004.202.01.06.004.124.012.193.025l-.005-.848a.296.296 0 0 0-.064-.02.562.562 0 0 0-.089-.025.978.978 0 0 0-.104-.014.455.455 0 0 0-.088-.01Zm2.934 0c-.253 0-.498.044-.735.133a1.604 1.604 0 0 0-.631.439c-.181.2-.327.464-.44.789-.11.322-.167.718-.167 1.188v.602c0 .408.048.761.143 1.06.096.3.232.548.41.745.18.194.399.339.655.434.257.095.545.143.863.143.23 0 .436-.023.617-.07a2.29 2.29 0 0 0 .488-.177c.141-.075.263-.16.365-.256.102-.095.189-.194.261-.296l-.453-.552c-.073.089-.15.17-.232.246a1.614 1.614 0 0 1-.271.193 1.422 1.422 0 0 1-.326.123 1.65 1.65 0 0 1-.4.044c-.404 0-.714-.13-.931-.39-.214-.259-.32-.675-.32-1.247v-.123h2.978v-.508c0-.408-.035-.768-.104-1.08a2.157 2.157 0 0 0-.33-.79 1.433 1.433 0 0 0-.582-.482 1.997 1.997 0 0 0-.858-.168Zm0 .75a.97.97 0 0 1 .473.103.762.762 0 0 1 .296.291c.076.125.13.274.163.449.036.17.059.36.069.567v.118h-2.1c.012-.299.048-.547.108-.744.059-.198.136-.354.231-.469a.79.79 0 0 1 .34-.247c.129-.046.269-.069.42-.069Z'
        />
      </g>
      <defs>
        <clipPath id='a'>
          <path fill='#fff' d='M0 0h48v46H0z' />
        </clipPath>
      </defs>
    </svg>
  );
};
